import { z } from 'zod';

/**
 * M&V & Savings contract (CLAUDE.md §10 baselines/opportunities/mv/enpis, §11.1 `/savings`, §14).
 *
 * Single source of truth for the M&V domain payloads — imported by the Worker routes, their
 * tests, the IPMVP calc lib, and the React `/savings` screen. Mutations are RBAC-gated:
 * `opportunities:write`, `baselines:write`, `baselines:approve`, `mv:verify`; reads are
 * `*:read`. Every mutation writes an immutable audit row (§12.3).
 *
 * IDs are generated server-side on create (callers never supply them). Timestamps are epoch ms.
 * A baseline is a regression of SEU energy vs. drivers (production, ambient, calendar); adjusted
 * savings = baseline-predicted − actual (IPMVP). Each verified saving carries its baseline id and
 * model stats (R²/CV-RMSE) plus a verifier sign-off for M&V defensibility.
 */

// ── Enums (mirror db/schema.ts $type unions) ────────────────────────────────
export const IpmvpOptionSchema = z.enum(['A', 'B', 'C', 'D']);
export type IpmvpOption = z.infer<typeof IpmvpOptionSchema>;

export const BaselineModelTypeSchema = z.enum(['ols', 'ridge', 'manual']);
export type BaselineModelType = z.infer<typeof BaselineModelTypeSchema>;

export const OpportunityStatusSchema = z.enum([
  'identified',
  'evaluating',
  'approved',
  'implementing',
  'verified',
  'rejected',
]);
export type OpportunityStatus = z.infer<typeof OpportunityStatusSchema>;

// ── SEU + EnPI read rows ─────────────────────────────────────────────────────
export const SeuRowSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  name: z.string(),
  category: z.string(),
  driverTagId: z.string().nullable(),
});
export type SeuRow = z.infer<typeof SeuRowSchema>;

export const EnpiRowSchema = z.object({
  id: z.string(),
  seuId: z.string(),
  name: z.string(),
  formula: z.string(),
  unit: z.string(),
  target: z.number().nullable(),
  baselineId: z.string().nullable(),
});
export type EnpiRow = z.infer<typeof EnpiRowSchema>;

// ── Baseline read row ────────────────────────────────────────────────────────
export const BaselineRowSchema = z.object({
  id: z.string(),
  seuId: z.string(),
  modelType: BaselineModelTypeSchema,
  /** Driver-name → coefficient map; `__intercept__` holds the constant term. */
  coeffs: z.record(z.number()).nullable(),
  r2: z.number().nullable(),
  cvRmse: z.number().nullable(),
  periodFrom: z.string().nullable(),
  periodTo: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: z.number().nullable(),
});
export type BaselineRow = z.infer<typeof BaselineRowSchema>;

// ── Opportunity read row ─────────────────────────────────────────────────────
export const OpportunityRowSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  name: z.string(),
  category: z.string(),
  estSavingPa: z.number().nullable(),
  ipmvpOption: IpmvpOptionSchema.nullable(),
  status: OpportunityStatusSchema,
  owner: z.string().nullable(),
  businessCaseUrl: z.string().nullable(),
});
export type OpportunityRow = z.infer<typeof OpportunityRowSchema>;

// ── M&V record read row ──────────────────────────────────────────────────────
export const MvRecordRowSchema = z.object({
  id: z.string(),
  opportunityId: z.string(),
  period: z.string(),
  baselineKwh: z.number().nullable(),
  actualKwh: z.number().nullable(),
  adjustedSaving: z.number().nullable(),
  co2eSaved: z.number().nullable(),
  verifiedBy: z.string().nullable(),
  verifiedAt: z.number().nullable(),
});
export type MvRecordRow = z.infer<typeof MvRecordRowSchema>;

// ── List responses ───────────────────────────────────────────────────────────
export const SeuListResponseSchema = z.object({ seus: z.array(SeuRowSchema) });
export type SeuListResponse = z.infer<typeof SeuListResponseSchema>;

export const EnpiListResponseSchema = z.object({ enpis: z.array(EnpiRowSchema) });
export type EnpiListResponse = z.infer<typeof EnpiListResponseSchema>;

export const BaselineListResponseSchema = z.object({ baselines: z.array(BaselineRowSchema) });
export type BaselineListResponse = z.infer<typeof BaselineListResponseSchema>;

export const OpportunityListResponseSchema = z.object({
  opportunities: z.array(OpportunityRowSchema),
});
export type OpportunityListResponse = z.infer<typeof OpportunityListResponseSchema>;

export const MvRecordListResponseSchema = z.object({ records: z.array(MvRecordRowSchema) });
export type MvRecordListResponse = z.infer<typeof MvRecordListResponseSchema>;

// ── Mutation inputs ──────────────────────────────────────────────────────────
/** One training observation: the SEU's measured energy and its driver values for a period. */
export const BaselineObservationSchema = z.object({
  energyKwh: z.number(),
  drivers: z.array(z.number()),
});
export type BaselineObservation = z.infer<typeof BaselineObservationSchema>;

/**
 * Create a baseline by fitting a regression of energy vs. drivers (§14). The server fits the
 * model (OLS or ridge), stores coeffs + R²/CV-RMSE, and never trusts client-supplied stats.
 * `driverNames` labels each driver column; every observation's `drivers` must match its length.
 */
export const BaselineCreateSchema = z
  .object({
    seuId: z.string().min(1),
    modelType: z.enum(['ols', 'ridge']),
    driverNames: z.array(z.string().min(1)).min(1),
    observations: z.array(BaselineObservationSchema).min(2),
    periodFrom: z.string().nullable().optional(),
    periodTo: z.string().nullable().optional(),
  })
  .refine((o) => o.observations.every((ob) => ob.drivers.length === o.driverNames.length), {
    message: 'each observation must have one value per driver name',
  })
  .refine((o) => o.observations.length > o.driverNames.length, {
    message: 'need more observations than drivers to fit a baseline',
  });
export type BaselineCreate = z.infer<typeof BaselineCreateSchema>;

export const OpportunityCreateSchema = z.object({
  siteId: z.string().min(1),
  name: z.string().min(1),
  category: z.string().min(1),
  estSavingPa: z.number().nullable().optional(),
  ipmvpOption: IpmvpOptionSchema.nullable().optional(),
  status: OpportunityStatusSchema.optional(),
  owner: z.string().nullable().optional(),
  businessCaseUrl: z.string().nullable().optional(),
});
export type OpportunityCreate = z.infer<typeof OpportunityCreateSchema>;

export const OpportunityUpdateSchema = OpportunityCreateSchema.omit({ siteId: true })
  .partial()
  .refine((o) => Object.keys(o).length > 0, { message: 'at least one field required' });
export type OpportunityUpdate = z.infer<typeof OpportunityUpdateSchema>;

/** Create an (unverified) M&V record for an opportunity + reporting period. */
export const MvRecordCreateSchema = z.object({
  opportunityId: z.string().min(1),
  period: z.string().min(1),
  baselineKwh: z.number().nullable().optional(),
  actualKwh: z.number().nullable().optional(),
});
export type MvRecordCreate = z.infer<typeof MvRecordCreateSchema>;

/**
 * Verify a record: server computes adjusted saving = baselineKwh − actualKwh and CO₂e saved,
 * stamps verifier + timestamp. `co2eFactorKgPerKwh` defaults to the site grid factor if omitted.
 */
export const MvVerifyInputSchema = z.object({
  actualKwh: z.number(),
  baselineKwh: z.number().optional(),
  co2eFactorKgPerKwh: z.number().nonnegative().optional(),
});
export type MvVerifyInput = z.infer<typeof MvVerifyInputSchema>;

// ── Single-row responses ─────────────────────────────────────────────────────
export const BaselineResponseSchema = z.object({ baseline: BaselineRowSchema });
export type BaselineResponse = z.infer<typeof BaselineResponseSchema>;
export const OpportunityResponseSchema = z.object({ opportunity: OpportunityRowSchema });
export type OpportunityResponse = z.infer<typeof OpportunityResponseSchema>;
export const MvRecordResponseSchema = z.object({ record: MvRecordRowSchema });
export type MvRecordResponse = z.infer<typeof MvRecordResponseSchema>;

// ── Savings summary (the `/savings` screen aggregate) ────────────────────────
export const SavingsCumulativePointSchema = z.object({
  /** Calendar-month key, YYYY-MM. */
  period: z.string(),
  /** Cumulative verified saving up to and including this period. */
  cumulativeKwh: z.number(),
  /** Cumulative target up to and including this period. */
  targetKwh: z.number(),
});
export type SavingsCumulativePoint = z.infer<typeof SavingsCumulativePointSchema>;

export const SavingsSummaryResponseSchema = z.object({
  siteId: z.string(),
  /** Total verified saving (sum of adjusted savings across verified records), kWh. */
  verifiedSavingKwh: z.number(),
  /** Verified saving valued at tariff, ZAR. */
  verifiedSavingZar: z.number(),
  /** Verified CO₂e saved, tonnes. */
  co2eSavedTonnes: z.number(),
  /** Annual savings target, kWh. */
  targetKwh: z.number(),
  /** verifiedSavingKwh / targetKwh × 100 (0 when target is 0). */
  attainmentPct: z.number(),
  cumulative: z.array(SavingsCumulativePointSchema),
  pipeline: z.array(OpportunityRowSchema),
  /** True when the screen is driven by seed/illustrative data (no verified records yet). */
  illustrative: z.boolean(),
});
export type SavingsSummaryResponse = z.infer<typeof SavingsSummaryResponseSchema>;
